// @flow
import { of } from 'rxjs';
import { catchError, mergeMap, map } from 'rxjs/operators';
import update from 'immutability-helper';
import { endpoints, getPaymentsApi, postPaymentApi, putPaymentApi, deletePaymentApi } from './api';
import { ofType } from 'redux-observable';

import type { Observable } from 'rxjs';
import type { HTTPError } from '../../../error';
import type { Payment } from '../../../entities';

export type PaymentsAction = {
  +type: string;
  +payment?: Payment;
  +payments?: Payment[];
  +paymentId?: string;
  +error?: HTTPError;
};

export type PaymentsState = {
  +isFetchingPayments?: boolean;
  +isPostingPayment?: boolean;
  +isPuttingPayment?: boolean;
  +isDeletingPayment?: boolean;
  +payments?: Payment[];
  +closePaymentModal?: boolean;
  +successDelete?: boolean;
  +fetchPaymentsError?: HTTPError;
  +postPaymentError?: HTTPError;
  +editPaymentError?: HTTPError;
  +deletePaymentError?: HTTPError;
};

// Actions
const PAYMENT_FETCH_PAYMENTS_REQUEST = 'PAYMENT_FETCH_PAYMENTS_REQUEST';
const PAYMENT_FETCH_PAYMENTS_SUCCESS = 'PAYMENT_FETCH_PAYMENTS_SUCCESS';
const PAYMENT_FETCH_PAYMENTS_ERROR = 'PAYMENT_FETCH_PAYMENTS_ERROR';
const PAYMENT_POST_PAYMENT_REQUEST = 'PAYMENT_POST_PAYMENT_REQUEST';
const PAYMENT_POST_PAYMENT_SUCCESS = 'PAYMENT_POST_PAYMENT_SUCCESS';
const PAYMENT_POST_PAYMENT_ERROR = 'PAYMENT_POST_PAYMENT_ERROR';
const PAYMENT_PUT_PAYMENT_REQUEST = 'PAYMENT_PUT_PAYMENT_REQUEST';
const PAYMENT_PUT_PAYMENT_SUCCESS = 'PAYMENT_PUT_PAYMENT_SUCCESS';
const PAYMENT_PUT_PAYMENT_ERROR = 'PAYMENT_PUT_PAYMENT_ERROR';
const PAYMENT_DELETE_PAYMENT_REQUEST = 'PAYMENT_DELETE_PAYMENT_REQUEST';
const PAYMENT_DELETE_PAYMENT_SUCCESS = 'PAYMENT_DELETE_PAYMENT_SUCCESS';
const PAYMENT_DELETE_PAYMENT_ERROR = 'PAYMENT_DELETE_PAYMENT_ERROR';

// Action creators
export function fetchPayments(): PaymentsAction {
  return {
    type: PAYMENT_FETCH_PAYMENTS_REQUEST,
  }
}

export function fetchPaymentsSuccess(payments: Payment[]): PaymentsAction {
  return {
    type: PAYMENT_FETCH_PAYMENTS_SUCCESS,
    payments,
  }
}

export function fetchPaymentsErrorFn(error: HTTPError): PaymentsAction {
  return {
    type: PAYMENT_FETCH_PAYMENTS_ERROR,
    error,
  }
}

export function postPayment(payment: Payment): PaymentsAction {
  return {
    type: PAYMENT_POST_PAYMENT_REQUEST,
    payment,
  }
}

export function postPaymentSuccess(payment: Payment): PaymentsAction {
  return {
    type: PAYMENT_POST_PAYMENT_SUCCESS,
    payment,
  }
}

export function postPaymentErrorFn(error: HTTPError): PaymentsAction {
  return {
    type: PAYMENT_POST_PAYMENT_ERROR,
    error,
  }
}

export function editPayment(payment: Payment): PaymentsAction {
  return {
    type: PAYMENT_PUT_PAYMENT_REQUEST,
    payment,
  }
}

export function editPaymentSuccess(payment: Payment): PaymentsAction {
  return {
    type: PAYMENT_PUT_PAYMENT_SUCCESS,
    payment,
  }
}

export function editPaymentErrorFn(error: HTTPError): PaymentsAction {
  return {
    type: PAYMENT_PUT_PAYMENT_ERROR,
    error,
  }
}

export function deletePayment(paymentId: string): PaymentsAction {
  return {
    type: PAYMENT_DELETE_PAYMENT_REQUEST,
    paymentId,
  }
}

export function deletePaymentSuccess(paymentId: string): PaymentsAction {
  return {
    type: PAYMENT_DELETE_PAYMENT_SUCCESS,
    paymentId,
  }
}

export function deletePaymentErrorFn(error: HTTPError): PaymentsAction {
  return {
    type: PAYMENT_DELETE_PAYMENT_ERROR,
    error,
  }
}

export const actions = {
  PAYMENT_FETCH_PAYMENTS_REQUEST,
  PAYMENT_FETCH_PAYMENTS_SUCCESS,
  PAYMENT_FETCH_PAYMENTS_ERROR,
  PAYMENT_POST_PAYMENT_REQUEST,
  PAYMENT_POST_PAYMENT_SUCCESS,
  PAYMENT_POST_PAYMENT_ERROR,
  PAYMENT_PUT_PAYMENT_REQUEST,
  PAYMENT_PUT_PAYMENT_SUCCESS,
  PAYMENT_PUT_PAYMENT_ERROR,
  PAYMENT_DELETE_PAYMENT_REQUEST,
  PAYMENT_DELETE_PAYMENT_SUCCESS,
  PAYMENT_DELETE_PAYMENT_ERROR,
  fetchPayments,
  fetchPaymentsSuccess,
  fetchPaymentsErrorFn,
  postPayment,
  postPaymentSuccess,
  postPaymentErrorFn,
  editPayment,
  editPaymentSuccess,
  editPaymentErrorFn,
  deletePayment,
  deletePaymentSuccess,
  deletePaymentErrorFn,
};

export const initialState: PaymentsState = {};

//Reducer
export default function reducer(state: PaymentsState = initialState, action: PaymentsAction): PaymentsState {
  switch (action.type) {
    case PAYMENT_FETCH_PAYMENTS_REQUEST:
      return {
        isFetchingPayments: true,
      };
    case PAYMENT_FETCH_PAYMENTS_SUCCESS:
      return {
        payments: action.payments,
      };
    case PAYMENT_FETCH_PAYMENTS_ERROR:
      return {
        fetchPaymentsError: action.error,
      };
    case PAYMENT_POST_PAYMENT_REQUEST:
      return update(state, {
        isPostingPayment: { $set: true },
        $unset: ['closePaymentModal'],
      });
    case PAYMENT_POST_PAYMENT_SUCCESS:
      return update(state, {
        $unset: ['isPostingPayment'],
        payments: { $push: [action.payment] },
        closePaymentModal: { $set: true },
      });
    case PAYMENT_POST_PAYMENT_ERROR:
      return update(state, {
        $unset: ['isPostingPayment', 'closePaymentModal'],
        postPaymentError: { $set: action.error },
      });
    case PAYMENT_PUT_PAYMENT_REQUEST:
      return update(state, {
        isPuttingPayment: { $set: true },
        $unset: ['closePaymentModal'],
      });
    case PAYMENT_PUT_PAYMENT_SUCCESS:
      if (!state.payments) return state;
      const putIndex = state.payments.findIndex((payment: Payment) =>
        action.payment ? payment.id === action.payment.id : -1
      );
      if (putIndex === -1) return state;
      return update(state, {
        $unset: ['isPuttingPayment'],
        payments: { [putIndex]: { $merge: action.payment } },
        closePaymentModal: { $set: true },
      });
    case PAYMENT_PUT_PAYMENT_ERROR:
      return update(state, {
        $unset: ['isPuttingPayment', 'closePaymentModal'],
        editPaymentError: { $set: action.error }
      });
    case PAYMENT_DELETE_PAYMENT_REQUEST:
      return update(state, {
        $unset: ['deletePaymentError', 'successDelete'],
        isDeletingPayment: { $set: true },
      });
    case PAYMENT_DELETE_PAYMENT_SUCCESS:
      if (!state.payments) return state;
      const deleteIndex = state.payments.findIndex((payment: Payment) =>
        action.paymentId === payment.id);
      return update(state, {
        $unset: ['isDeletingPayment'],
        payments: { $splice: [[deleteIndex, 1]] },
        successDelete: { $set: true },
      });
    case PAYMENT_DELETE_PAYMENT_ERROR:
      return update(state, {
        $unset: ['deletePaymentError', 'successDelete'],
        isDeletingPayment: { $set: true },
      });
    default:
      return state;
  }
}

// Epics
/*export function fetchPaymentsMadeEpic(action$: ActionsObservable<PaymentsAction>) {
  return action$.pipe(
ofType(PAYMENT_FETCH_PAYMENTS_REQUEST)
    .mergeMap(() => getPaymentsApi()
      .map((payments: Payment[]) => fetchPaymentsSuccess(payments))
      .catch((e: HTTPError) => Observable.of(fetchPaymentsErrorFn(e))));
}*/

export function postPaymentEpic(action$: Observable<PaymentsAction>): Observable<PaymentsAction> {
  return action$.pipe(
    ofType(PAYMENT_POST_PAYMENT_REQUEST),
    mergeMap(({ payment }) => postPaymentApi(payment).pipe(
      map((payment: Payment) => postPaymentSuccess(payment)),
      catchError((e: HTTPError) => of(postPaymentErrorFn(e))))
    ));
}

export function editPaymentEpic(action$: Observable<PaymentsAction>): Observable<PaymentsAction> {
  return action$.pipe(
    ofType(PAYMENT_PUT_PAYMENT_REQUEST),
    mergeMap(({ payment }) => {
      return putPaymentApi(payment, {
        route: endpoints.PUT_PAYMENT.replace(':id', payment.id),
      }).pipe(
        map(() => editPaymentSuccess(payment)),
        catchError((e: HTTPError) => of(editPaymentErrorFn(e))))
    }));
}

export function deletePaymentEpic(action$: Observable<PaymentsAction>): Observable<PaymentsAction> {
  return action$.pipe(
    ofType(PAYMENT_DELETE_PAYMENT_REQUEST),
    mergeMap(({ paymentId }) => deletePaymentApi(paymentId, {
        route: endpoints.DELETE_PAYMENT.replace(':id', paymentId),
      }).pipe(
      map(() => deletePaymentSuccess(paymentId)),
      catchError((e: HTTPError) => of(deletePaymentErrorFn(e))))
    ));
}



// WEBPACK FOOTER //
// ./src/modules/preferences/payments/duck.js